var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback } from 'react';
import { Form } from '@/components/functional/Form/Form';
import { MultiTextField } from '@/components/functional/Form/MultiTextField';
import { SubmitButton } from '@/components/functional/Form/SubmitButton';
import { UserBox } from '@/components/modules/Image/UserBox';
import { Button } from '@/components/ui/Button/Button';
import { CheckboxAgreement } from '@/components/ui/Form/CheckboxAgreement';
import { FieldLabel } from '@/components/ui/Form/FieldLabel';
import { ModalWithTitle } from '@/components/ui/Modal/ModalWithTitle';
import { hotlineCancelModalStyle } from '@/feature/hotline/component/HotlineCancelModal/style';
import { hotlineText } from '@/feature/hotline/constant/text';
import { hotlineCancelFormSchema } from '@/feature/hotline/util/hotlineCancelFormSchema';
import { useBoolState } from '@/hooks/state/useBoolState';
import { TERMS_HOTLINE_CANCEL_POLICY_SECTION_ID } from '@/utils/constants/static/tpl/terms';
import { FIELD_DOM_CLASS_NAME } from '@/utils/form/scroll';
import { zodFinalFormValidate } from '@/utils/libs/zod/validate';
import { pagePath } from '@/utils/router/pagePath';
const warningPoint = {
    client: [
        '相談日程の変更・キャンセルは、相談開始予定時刻の24時間前まで可能です。',
        'それ以降はキャンセル料金（相談費用の100%）がかかりますので、ご注意ください。',
    ],
    professional: [
        '相談をキャンセルする場合は、その理由を明確にクライアントへお伝えください。',
        'キャンセル後もチャット欄でやりとりを行うことが出来ます。',
    ],
};
/**
 * 匿名コンサルホットライン キャンセルモーダル
 */
export const HotlineCancelModal = (props) => {
    const { className = '', isShow, onClickBack: propsOnClickBack, onSubmit: propsOnSubmit, type, userList, } = props;
    /** クライアント画面か */
    const isClient = type === 'client';
    /** ユーザー表示があるか */
    const hasUserList = !!userList && userList.length > 0;
    // 同意チェックボックスの状態管理
    const { boolState: checked, changeFalse, changeToggle, changeTrue, } = useBoolState({
        initialState: !isClient,
    });
    /**
     * チェックボックスを初期状態に戻す
     */
    const resetChecked = useCallback(() => {
        if (isClient) {
            changeFalse();
        }
        else {
            changeTrue();
        }
    }, [changeFalse, changeTrue, isClient]);
    /**
     * 戻るボタン押下時の処理
     */
    const onClickBack = useCallback(() => {
        propsOnClickBack();
        resetChecked();
    }, [propsOnClickBack, resetChecked]);
    /**
     * 送信処理
     */
    const onSubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        yield propsOnSubmit(values);
        resetChecked();
    }), [propsOnSubmit, resetChecked]);
    const style = hotlineCancelModalStyle({ hasUserList });
    return (_jsx(ModalWithTitle, Object.assign({ contentClassName: style.base({ class: className }), headText: '相談をキャンセルする', isShow: isShow, maxWidth: 'max-w-[590px]' }, { children: _jsx(Form, { onSubmit: onSubmit, render: ({ handleSubmit, submitting }) => (_jsxs("form", Object.assign({ className: style.contentWrap(), onSubmit: handleSubmit }, { children: [_jsxs("dl", Object.assign({ className: style.descriptionContentWrap() }, { children: [_jsx("dt", Object.assign({ className: style.descriptionTitle() }, { children: "\u76F8\u8AC7\u30AD\u30E3\u30F3\u30BB\u30EB\u306B\u95A2\u3059\u308B\u6CE8\u610F\u70B9" })), _jsx("dd", Object.assign({ className: style.warningPointContent() }, { children: _jsx("ul", Object.assign({ className: style.warningPointList() }, { children: warningPoint[type].map((point, index) => (_jsx("li", Object.assign({ className: style.warningPointListItem() }, { children: point }), `HotlineCancelModal-warningPoint-${index + 1}`))) })) }))] })), hasUserList && (_jsxs("dl", Object.assign({ className: style.descriptionContentWrap() }, { children: [_jsx("dt", Object.assign({ className: style.descriptionTitle() }, { children: "\u4E0B\u8A18\u306E\u30B3\u30F3\u30B5\u30EB\u30BF\u30F3\u30C8\u3092\u30AD\u30E3\u30F3\u30BB\u30EB" })), _jsx("dd", Object.assign({ className: style.cancelUserListWrap() }, { children: _jsx("div", Object.assign({ className: style.cancelUserList() }, { children: userList.map((item) => (_createElement(UserBox, Object.assign({}, item, { avatarSize: 'md', className: style.cancelUserListItems(), isNestedLink: true, key: item.id })))) })) }))] }))), _jsx("div", { children: _jsx(FieldLabel, Object.assign({ className: FIELD_DOM_CLASS_NAME, isRequired: true, labelText: hotlineText.label.cancelReason() }, { children: _jsx(MultiTextField, { minRows: 4, name: 'cancelReason', placeholder: 'キャンセルの理由を入力してください' }) })) }), isClient && (_jsx(CheckboxAgreement, { checked: checked, className: style.checkbox(), linkText: 'キャンセルポリシー', onChange: changeToggle, to: `${pagePath.terms()}#${TERMS_HOTLINE_CANCEL_POLICY_SECTION_ID}` })), _jsxs("div", Object.assign({ className: style.buttonWrap() }, { children: [_jsx(SubmitButton, Object.assign({ className: style.button(), color: 'orange', disabled: !checked || submitting, isFull: true, loading: submitting, size: 'md' }, { children: hasUserList
                                    ? hotlineText.action.bulkCancel()
                                    : hotlineText.action.cancel() })), _jsx(Button, Object.assign({ className: style.button(), color: 'gray', disabled: submitting, isFull: true, onClick: onClickBack, size: 'md' }, { children: "\u623B\u308B" }))] }))] }))), validate: zodFinalFormValidate(hotlineCancelFormSchema) }) })));
};
